import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useFetchUserData } from 'queries/auth/user';
import { useFetchBranchContext as useFetchOwnerBranchContext } from 'queries/owner/owner';
import { isUserAdminOrOwner } from 'utils/roleConfig';

export interface Branch {
  id: number;
  name: string;
  is_main: boolean;
  branch_name?: string;
  is_main_branch?: boolean;
  location?: string;
}

interface BranchContextType {
  selectedBranchId: number | null;
  selectedBranch: Branch | null;
  branches: Branch[];
  setSelectedBranchId: (id: number) => void;
  isLoading: boolean;
  isOwner: boolean;
  isBranchAdmin: boolean;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

export const BranchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userData } = useFetchUserData();
  const { branchContext, loading: loadingContext } = useFetchOwnerBranchContext();
  
  const [selectedBranchId, setSelectedBranchIdState] = useState<number | null>(() => {
    const saved = localStorage.getItem('fuko_selected_branch_id');
    return saved ? parseInt(saved, 10) : null;
  });

  // Sync with backend context or user data if nothing selected or context changed
  useEffect(() => {
    // 1. Resolve potential default IDs
    const defaultId = branchContext?.primary_branch_id || userData?.branch_id;
    const branchesList = branchContext?.branches || [];

    // 2. Validate current selection against available branches if branches list is loaded
    if (branchesList.length > 0 && selectedBranchId) {
      const isValid = branchesList.some((b: Branch) => b.id === selectedBranchId);
      if (!isValid && defaultId) {
        setSelectedBranchIdState(defaultId);
        const defaultStr = defaultId.toString();
        localStorage.setItem('fuko_selected_branch_id', defaultStr);
        localStorage.setItem('active_branch_id', defaultStr);
        localStorage.setItem('branch_id', defaultStr);
        return;
      }
    }

    // 3. Initial Resolution: If nothing in state and we have a backend default, use it
    if (!selectedBranchId && defaultId) {
      setSelectedBranchIdState(defaultId);
      const defaultStr = defaultId.toString();
      localStorage.setItem('fuko_selected_branch_id', defaultStr);
      localStorage.setItem('active_branch_id', defaultStr);
      localStorage.setItem('branch_id', defaultStr);
      return;
    }

    // 4. 🛡️ Staff & Branch Admin Lock: Only master shop owners/admins can switch branches
    const isMasterOwnerOrAdmin = isUserAdminOrOwner(userData) || Boolean(branchContext?.is_owner);
    if (userData && !isMasterOwnerOrAdmin && userData.branch_id && selectedBranchId !== userData.branch_id) {
        const userBIdStr = userData.branch_id.toString();
        setSelectedBranchIdState(userData.branch_id);
        localStorage.setItem('fuko_selected_branch_id', userBIdStr);
        localStorage.setItem('active_branch_id', userBIdStr);
        localStorage.setItem('branch_id', userBIdStr);
    }
  }, [userData, selectedBranchId, branchContext]);

  const setSelectedBranchId = (id: number) => {
    setSelectedBranchIdState(id);
    const idStr = id.toString();
    localStorage.setItem('fuko_selected_branch_id', idStr);
    localStorage.setItem('active_branch_id', idStr);
    localStorage.setItem('branch_id', idStr);
    // Safe-Sync: Force reload to clear all caches and pick up new header across all components
    window.location.reload();
  };

  const branches = useMemo(() => branchContext?.branches || [], [branchContext]);
  
  const selectedBranch = useMemo(() => {
    return branches.find((b: Branch) => b.id === selectedBranchId) || null;
  }, [branches, selectedBranchId]);

  const value = useMemo(() => ({
    selectedBranchId,
    selectedBranch,
    branches,
    setSelectedBranchId,
    isLoading: loadingContext,
    isOwner: branchContext?.is_owner || false,
    isBranchAdmin: branchContext?.is_branch_admin || false,
  }), [selectedBranchId, selectedBranch, branches, loadingContext, branchContext]);

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
};

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};
